import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../utils/api';
import { GitProvider, providerLabel } from '../config/providers';
import { useEnabledProviders } from '../hooks/useEnabledProviders';

export type IntegrationProvider = GitProvider;

/** Session-storage key tracking whether a live credential check ran this session for a provider. */
export const validationSessionKey = (orgId: string, provider: IntegrationProvider): string =>
  `integration_checked_${orgId}_${provider}`;

/** @deprecated use validationSessionKey(orgId, 'azure_devops') */
export const adoSessionKey = (orgId: string): string => validationSessionKey(orgId, 'azure_devops');

/** Providers whose status endpoint performs a live credential check when asked (`validate=true`). */
const VALIDATING_PROVIDERS: readonly IntegrationProvider[] = ['azure_devops', 'codecommit'];

/** localStorage key used to remember that a GitHub/GitLab integration was previously connected. */
export const wasConnectedKey = (orgId: string, provider: 'github' | 'gitlab'): string =>
  `org_${orgId}_${provider}_was_connected`;

type StatusApi = { get: <T = any>(endpoint: string) => Promise<T> };

/** Per-provider status fetch. Providers without an entry are not polled (no UI yet). */
const STATUS_FETCHERS: Partial<Record<IntegrationProvider, (api: StatusApi, orgId: string, validate: boolean) => Promise<any>>> = {
  github: (api, orgId) => api.get(apiEndpoints.orgGithub.status(orgId)),
  gitlab: (api, orgId) => api.get(apiEndpoints.orgGitlab.status(orgId)),
  azure_devops: (api, orgId, validate) => api.get(apiEndpoints.orgAzureDevOps.status(orgId, validate)),
  codecommit: (api, orgId, validate) => api.get(apiEndpoints.orgCodeCommit.status(orgId, validate)),
};

/** OAuth providers: remember a successful connection so a later "not connected" is surfaced as an issue. */
const oauthEvaluator =
  (provider: 'github' | 'gitlab') =>
  (res: any, orgId: string): string | null => {
    if (res.connected) {
      localStorage.setItem(wasConnectedKey(orgId, provider), '1');
      return null;
    }
    return `Your ${providerLabel(provider)} integration is not connected.`;
  };

/** Turn a status response into a user-facing issue string, or null when healthy. */
const STATUS_EVALUATORS: Partial<Record<IntegrationProvider, (res: any, orgId: string) => string | null>> = {
  github: oauthEvaluator('github'),
  gitlab: oauthEvaluator('gitlab'),
  azure_devops: (res) => {
    if (!res.connected) return 'Azure DevOps is not connected.';
    if (res.pat_invalid) return 'Your Azure DevOps PAT is invalid or revoked.';
    if (res.token_expires_at) {
      const expiresAt = new Date(res.token_expires_at);
      const days = Math.ceil((expiresAt.getTime() - Date.now()) / 86400000);
      if (expiresAt < new Date()) return 'Your Azure DevOps PAT has expired.';
      if (days <= 7) return `Your Azure DevOps PAT expires in ${days} day${days === 1 ? '' : 's'}.`;
    }
    return null;
  },
  codecommit: (res) => {
    if (!res.connected) return 'AWS CodeCommit is not connected.';
    const invalid = (res.connections ?? []).find((c: any) => c?.credentials_invalid);
    if (invalid) {
      return `Your AWS CodeCommit credentials for ${invalid.aws_account_id} (${invalid.aws_region}) are invalid or revoked.`;
    }
    return null;
  },
};

interface IntegrationStatusContextType {
  issues: Map<IntegrationProvider, string>;
  refresh: () => void;
}

export const IntegrationStatusContext = createContext<IntegrationStatusContextType>({
  issues: new Map(),
  refresh: () => {},
});

export const IntegrationStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentOrganization } = useOrganization();
  const { isAuthenticated } = useAuth();
  const api = useAuthenticatedApi();
  const enabledProviders = useEnabledProviders();

  const [issues, setIssues] = useState<Map<IntegrationProvider, string>>(new Map());
  const [refreshTick, setRefreshTick] = useState(0);

  const orgId = currentOrganization?.id;

  useEffect(() => {
    if (!isAuthenticated || !orgId) {
      setIssues(new Map());
      return;
    }

    setIssues(new Map());

    let cancelled = false;

    // Credential-based providers: live-validate once per session per org.
    // Written inside the effect (not during render) to avoid StrictMode double-render issues.
    // refreshTick > 0 means refresh() was called — session keys were already cleared by refresh().
    const shouldValidate = (provider: IntegrationProvider): boolean => {
      if (!VALIDATING_PROVIDERS.includes(provider)) return false;
      const key = validationSessionKey(orgId, provider);
      if (sessionStorage.getItem(key) === '1') return false;
      sessionStorage.setItem(key, '1');
      return true;
    };

    // Only poll providers that are enabled for this deployment and have a status endpoint wired up.
    const fetches: [IntegrationProvider, Promise<any>][] = enabledProviders.flatMap((provider) => {
      const fetcher = STATUS_FETCHERS[provider];
      return fetcher
        ? [[provider, fetcher(api, orgId, shouldValidate(provider))] as [IntegrationProvider, Promise<any>]]
        : [];
    });

    Promise.allSettled(fetches.map(([, p]) => p)).then((results) => {
      if (cancelled) return;
      const found = new Map<IntegrationProvider, string>();

      results.forEach((result, i) => {
        const [provider] = fetches[i];
        if (result.status !== 'fulfilled' || !result.value) return;
        const issue = STATUS_EVALUATORS[provider]?.(result.value, orgId) ?? null;
        if (issue) found.set(provider, issue);
      });

      setIssues(found);
    });

    return () => {
      cancelled = true;
    };
  }, [orgId, refreshTick, api, isAuthenticated, enabledProviders]);

  const refresh = useCallback(() => {
    if (orgId) {
      VALIDATING_PROVIDERS.forEach((provider) => sessionStorage.removeItem(validationSessionKey(orgId, provider)));
    }
    setRefreshTick((t) => t + 1);
  }, [orgId]);

  const value = useMemo(
    () => ({ issues, refresh }),
    [issues, refresh],
  );

  return (
    <IntegrationStatusContext.Provider value={value}>
      {children}
    </IntegrationStatusContext.Provider>
  );
};

export const useIntegrationStatus = () => useContext(IntegrationStatusContext);
